import type { ReactNode } from "react";
import * as Dialog from "@/components/ui/dialog";
import * as Drawer from "@/components/ui/drawer";
import { useMobile } from "@/providers/mobileProvider";

export type ResponsiveDialogDrawerProps = {
  title?: string;
  description?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  className?: string;
};

/**
 * Responsive component to switch between Dialog (Desktop) and Drawer (Mobile)
 */
export const ResponsiveDialogDrawer = ({
  title,
  description,
  open,
  onOpenChange,
  children,
  className,
}: ResponsiveDialogDrawerProps) => {
  const { isMobileScreen } = useMobile();

  const { Container, Content, Header, Title, Description, Footer, Close } = isMobileScreen
    ? {
        Container: Drawer.Drawer,
        Content: Drawer.DrawerContent,
        Header: Drawer.DrawerHeader,
        Title: Drawer.DrawerTitle,
        Description: Drawer.DrawerDescription,
        Footer: Drawer.DrawerFooter,
        Close: Drawer.DrawerClose,
      }
    : {
        Container: Dialog.Dialog,
        Content: Dialog.DialogContent,
        Header: Dialog.DialogHeader,
        Title: Dialog.DialogTitle,
        Description: Dialog.DialogDescription,
        Footer: Dialog.DialogFooter,
        Close: Dialog.DialogClose,
      };

  return (
    <Container open={open} onOpenChange={onOpenChange}>
      <Content className={className}>
        <div className="overflow-y-auto">
          <Header>
            <Title className="capitalize hidden">{title}</Title>
            {description && <Description>{description}</Description>}
          </Header>
          <div className="px-4 md:px-0 md:py-4">{children}</div>
          <Footer>
            <Close asChild />
          </Footer>
        </div>
      </Content>
    </Container>
  );
};
